import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  DatePicker,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
  TimePicker,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatDateTime } from '../../../utils/dateFormat';
import type { Newsletter, NewsletterSendRequest, NewsletterSummary } from '../../../types/models/Newsletter';
import type { ApiResponseNewsletterSingle } from '../../../types/api/ApiResponseNewsletter';

interface NewsletterSendModalProps {
  newsletter: NewsletterSummary;
  onClose: () => void;
  /** Hand back the queued newsletter so the parent updates its row (status → queued). */
  onSent: (updated: Newsletter) => void;
}

type SendTiming = 'immediate' | 'schedule';

const pad = (n: number): string => n.toString().padStart(2, '0');
/** Local calendar day of `d` at 00:00 — matches DatePicker's own `YYYY-MM-DDT00:00:00` parse. */
const startOfLocalDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
/** `YYYY-MM-DD` — the DatePicker's default text format. */
const toDateValue = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** `HH:MM` (24h) — the TimePicker text format when `is24Hour`. */
const toTimeValue = (d: Date): string => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Sensible default when the user opts to schedule: the next full hour, one hour out. */
const defaultScheduleDate = (): Date => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return d;
};

/**
 * Confirm-and-send modal. `POST …/send` only QUEUES a draft (`draft` → `queued`) after checking it
 * renders; the background worker (`send-newsletters.php`, gated by NEWSLETTER_ENABLED) does the
 * real broadcast to every opt-in user out-of-band. Only a plain draft can be queued.
 *
 * Two timings (backend `not_before` on the send body):
 *   • **Send immediately** — body `{ not_before: null }` clears any schedule so the worker sends it on
 *     its next tick.
 *   • **Schedule for later** — body `{ not_before: <ISO-8601> }`; the row stays `queued`
 *     (`is_scheduled: true`) until due. A scheduled send is cancellable by deleting the queued row.
 *
 * The chosen local wall-clock is sent as an absolute ISO instant (`Date.toISOString()`), which the
 * backend normalizes to its `APP_TIMEZONE` — so the send fires at the moment the staffer picked.
 */
function NewsletterSendModal({ newsletter, onClose, onSent }: NewsletterSendModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [timing, setTiming] = useState<SendTiming>('immediate');
  const [dateValue, setDateValue] = useState('');
  const [dateObj, setDateObj] = useState<Date | null>(null);
  const [timeValue, setTimeValue] = useState('');
  const [timeHour, setTimeHour] = useState<number | null>(null);
  const [timeMinute, setTimeMinute] = useState<number | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  // Combine the two pickers into one instant; null until both a valid date and a valid time are set.
  const scheduledAt = useMemo<Date | null>(() => {
    if (dateObj === null || timeHour === null || timeMinute === null) {
      return null;
    }
    const combined = new Date(dateObj);
    combined.setHours(timeHour, timeMinute, 0, 0);
    return combined;
  }, [dateObj, timeHour, timeMinute]);

  const isFutureSchedule = scheduledAt !== null && scheduledAt.getTime() > Date.now();
  const scheduleTouched = dateValue.trim() !== '' || timeValue.trim() !== '';

  // Inline validation shown under the pickers (only in schedule mode, and only once the user has begun).
  const scheduleError =
    timing !== 'schedule' || !scheduleTouched
      ? null
      : scheduledAt === null
        ? 'Enter a valid date and time.'
        : !isFutureSchedule
          ? 'Choose a date and time in the future.'
          : null;

  const canSend = !isSending && (timing === 'immediate' || isFutureSchedule);

  const selectSchedule = () => {
    // Seed friendly defaults the first time the user switches to scheduling.
    if (dateValue === '' && timeValue === '') {
      const seed = defaultScheduleDate();
      setDateValue(toDateValue(seed));
      setDateObj(startOfLocalDay(seed));
      setTimeValue(toTimeValue(seed));
      setTimeHour(seed.getHours());
      setTimeMinute(seed.getMinutes());
    }
    setTiming('schedule');
  };

  const onDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string, date?: Date) => {
    setError(null);
    setDateValue(value);
    setDateObj(date !== undefined && !Number.isNaN(date.getTime()) ? startOfLocalDay(date) : null);
  };

  const onTimeChange = (
    _event: React.FormEvent<HTMLInputElement>,
    time: string,
    hour?: number,
    minute?: number,
    _seconds?: number,
    isValid?: boolean,
  ) => {
    setError(null);
    setTimeValue(time);
    if (isValid !== false && typeof hour === 'number' && typeof minute === 'number') {
      setTimeHour(hour);
      setTimeMinute(minute);
    } else {
      setTimeHour(null);
      setTimeMinute(null);
    }
  };

  // Disable past days in the calendar popover and reject a typed past date.
  const dateValidators = useMemo(
    () => [(date: Date): string => (date < startOfLocalDay(new Date()) ? 'The date is in the past.' : '')],
    [],
  );

  const handleSend = async () => {
    setIsSending(true);
    setError(null);
    try {
      // Immediate: send `{ not_before: null }` to CLEAR any schedule the draft may carry (an omitted
      // key would preserve it). Schedule: the picked instant as ISO-8601 for the backend to normalize.
      const requestBody: NewsletterSendRequest =
        timing === 'schedule' && scheduledAt !== null ? { not_before: scheduledAt.toISOString() } : { not_before: null };

      const response = await apiFetch(`/admin/newsletters/${newsletter.id}/send`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      // Branch on the JSend `body.status`, NEVER `response.ok`.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterSingle | null;

      if (body?.status === 'success' && body.data?.newsletter) {
        const updated = body.data.newsletter;
        onSent(updated);
        const scheduledFor =
          updated.is_scheduled && updated.not_before !== null ? formatDateTime(updated.not_before) : null;
        addNotification({
          title: scheduledFor !== null ? 'Newsletter scheduled' : 'Newsletter queued',
          srTitle: 'Success alert',
          description:
            scheduledFor !== null
              ? `${newsletter.subject} is scheduled to send on ${scheduledFor} to every opted-in recipient.`
              : `${newsletter.subject} is queued — the background service will send it to every opted-in recipient.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onClose();
        return;
      }

      // Field errors: `not_before` (bad datetime), `newsletter` (not a draft), `template` (won't render).
      const fieldMessage =
        typeof body?.data?.not_before === 'string'
          ? (body.data.not_before as string)
          : typeof body?.data?.newsletter === 'string'
            ? (body.data.newsletter as string)
            : typeof body?.data?.template === 'string'
              ? (body.data.template as string)
              : null;
      const message = fieldMessage ?? body?.message ?? 'Could not queue the newsletter. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not queue newsletter',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not queue newsletter',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal variant="medium" isOpen onClose={onClose}>
      <ModalHeader title="Send newsletter" titleIconVariant="warning" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Could not queue" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content component="p" className="pf-v6-u-mb-md">
          <strong>{newsletter.subject}</strong> will be queued for the background broadcast — rendered once and emailed
          to <strong>every opted-in recipient</strong>. Choose to send it right away or schedule it for later; a
          scheduled send stays queued until its time and can be cancelled by deleting it before then.
        </Content>
        <Form>
          <FormGroup label="When to send" fieldId="newsletter-send-timing">
            <div role="radiogroup" aria-label="When to send the newsletter">
              <Radio
                id="newsletter-send-immediate"
                name="newsletter-send-timing"
                label="Send immediately"
                description="The next send run (within ~5 minutes) emails it to every recipient."
                isChecked={timing === 'immediate'}
                onChange={() => setTiming('immediate')}
                isDisabled={isSending}
                className="pf-v6-u-mb-sm"
              />
              <Radio
                id="newsletter-send-schedule"
                name="newsletter-send-timing"
                label="Schedule for later"
                description="Hold the newsletter and send it at a chosen date and time."
                isChecked={timing === 'schedule'}
                onChange={() => selectSchedule()}
                isDisabled={isSending}
              />
            </div>
          </FormGroup>

          {timing === 'schedule' && (
            <FormGroup label="Send at" isRequired fieldId="newsletter-schedule-date">
              <Flex
                spaceItems={{ default: 'spaceItemsSm' }}
                alignItems={{ default: 'alignItemsFlexStart' }}
                flexWrap={{ default: 'wrap' }}
              >
                <FlexItem>
                  <DatePicker
                    id="newsletter-schedule-date"
                    aria-label="Scheduled send date"
                    value={dateValue}
                    onChange={onDateChange}
                    validators={dateValidators}
                    isDisabled={isSending}
                    placeholder="YYYY-MM-DD"
                  />
                </FlexItem>
                <FlexItem>
                  <TimePicker
                    id="newsletter-schedule-time"
                    aria-label="Scheduled send time"
                    time={timeValue}
                    onChange={onTimeChange}
                    is24Hour
                    menuAppendTo="parent"
                    isDisabled={isSending}
                    placeholder="HH:MM"
                  />
                </FlexItem>
              </Flex>
              <FormHelperText>
                <HelperText>
                  {scheduleError !== null ? (
                    <HelperTextItem variant="error">{scheduleError}</HelperTextItem>
                  ) : (
                    <HelperTextItem>Interpreted in your local time. The send fires on the next run after this moment.</HelperTextItem>
                  )}
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={() => void handleSend()} isLoading={isSending} isDisabled={!canSend}>
          {timing === 'schedule' ? 'Schedule send' : 'Queue for sending'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSending}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NewsletterSendModal;
